import React from "react";
import { Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppGuide } from "../AppGuideProvider";
import { useTour } from "~/components/Tour";
import { getTourForRoute } from "~/components/Tour/utils/getTourForRoute";
import { ROUTES } from "~/utils/routing/routeUtils";
import type { JourneySectionKey } from "./journeySectionTypes";
import type { JourneySectionTitleKey } from "./journeySectionTypes";

interface JourneySectionFrameProps {
  sectionKey: JourneySectionKey;
  titleKey: JourneySectionTitleKey;
  showMePath: string;
  isMobile: boolean;
  children: React.ReactNode;
}

/**
 * Shared section chrome: kicker + Show me CTA that closes the guide and
 * starts the matching page tour when one exists.
 * Welcome also offers "Let's set it up" to start the portfolio setup walkthrough.
 */
export const JourneySectionFrame: React.FC<JourneySectionFrameProps> = ({
  sectionKey,
  titleKey,
  showMePath,
  isMobile,
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeGuide, markAsSeen } = useAppGuide();
  const { setPendingTour, startSetupWalkthrough } = useTour();

  const handleShowMe = () => {
    markAsSeen();
    closeGuide();
    const hasTour = getTourForRoute(showMePath, t) != null;
    if (hasTour) {
      setPendingTour({ path: showMePath });
    }
    navigate(showMePath);
  };

  const handleLetsSetItUp = () => {
    markAsSeen();
    closeGuide();
    startSetupWalkthrough();
    setPendingTour({ path: ROUTES.propertyTable });
    navigate(ROUTES.propertyTable);
  };

  const isWelcome = sectionKey === "welcome";

  return (
    <div
      className={`journey-section${isMobile ? " is-mobile" : ""}`}
      data-journey-section={sectionKey}
    >
      <p className="journey-section-kicker">{t(titleKey)}</p>
      <div className="journey-section-body">{children}</div>
      <div className="journey-section-cta">
        {isWelcome ? (
          <Space
            direction={isMobile ? "vertical" : "horizontal"}
            style={{ width: isMobile ? "100%" : undefined }}
            wrap={!isMobile}
          >
            <Button
              type="primary"
              block={isMobile}
              onClick={handleLetsSetItUp}
            >
              {t("JourneyGuide_LetsSetItUp")}
            </Button>
            <Button block={isMobile} onClick={handleShowMe}>
              {t("JourneyGuide_ShowMe")}
            </Button>
          </Space>
        ) : (
          <Button type="primary" block={isMobile} onClick={handleShowMe}>
            {t("JourneyGuide_ShowMe")}
          </Button>
        )}
      </div>
    </div>
  );
};
